import React, { useState, useEffect } from 'react';
import styles from './verifactu-modal.module.css';
import Button from '../../ui/button';
import { MyIcons } from '../../icons/my-icons';
import { showErrorToast } from '../../../utils/notifications';
import { API_BASE_URL } from '../../../config';

/**
 * Modal para visualizar archivos de Verifactu
 */
export default function VerifactuModal({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Cargar archivos de Verifactu
  useEffect(() => {
    const loadFiles = async () => {
      if (isOpen) {
        try {
          setLoading(true);
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/archivos/declaracionResponsable`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          
          if (!response.ok) {
            throw new Error(`Error al obtener archivos: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('Verifactu files loaded:', data);
          setFiles(data);
        } catch (error) {
          console.error('Error loading Verifactu files:', error);
          showErrorToast('Error al cargar los archivos de Verifactu');
        } finally {
          setLoading(false);
        }
      }
    };

    loadFiles();
  }, [isOpen]);

  const handleClose = () => {
    setSelectedFile(null);
    setIsFullscreen(false);
    onClose();
  };

  const handleFileClick = (file) => {
    console.log('Verifactu: Archivo seleccionado:', file);
    setSelectedFile(file);
  };

  const handleCloseViewer = () => {
    setSelectedFile(null);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (ext === "pdf") {
      return <MyIcons name="pdf" width={48} height={48} color="var(--dark-grey)" />;
    }
    if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
      return <MyIcons name="image" width={48} height={48} color="var(--dark-grey)" />;
    }
    if (["doc", "docx"].includes(ext)) {
      return <MyIcons name="fileFilled" width={48} height={48} color="var(--main)" />;
    }
    return <MyIcons name="fileFilled" width={48} height={48} color="var(--dark-grey)" />;
  };

  const FileViewer = ({ file }) => {
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    
    const ext = (file.name || '').split('.').pop().toLowerCase();
    const isPDF = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    const canFullscreen = isPDF || isImage;

    // Construir URL del archivo
    const fileUrl = file.url.startsWith('http') ? file.url : `${API_BASE_URL}${file.url}`;

    // When file changes, fetch with auth and create blob URL for preview (igual que SAMBA)
    useEffect(() => {
      let mounted = true;
      let objectUrl = null;

      const loadPreview = async () => {
        setPreviewLoading(true);
        setPreviewError(null);

        try {
          console.log('Verifactu: intentando URL de previsualización ->', fileUrl);
          const token = localStorage.getItem('authToken');
          const res = await fetch(fileUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          console.log('Verifactu: respuesta status ->', res.status);

          if (!res.ok) {
            throw new Error(`Error ${res.status}`);
          }

          const blob = await res.blob();
          console.log('Verifactu: blob cargado, tipo:', blob.type, 'tamaño:', blob.size);

          // Create object URL for preview
          objectUrl = URL.createObjectURL(blob);
          if (!mounted) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          console.log('Verifactu: blob URL creada ->', objectUrl);
          setPreviewBlobUrl(objectUrl);
          setPreviewLoading(false);
          setPreviewError(null);
        } catch (err) {
          console.error('Verifactu: error cargando preview:', err);
          if (mounted) {
            setPreviewError('No se pudo cargar la previsualización');
            setPreviewLoading(false);
          }
        }
      };

      loadPreview();

      return () => {
        mounted = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }, [file, fileUrl]);

    // Render content
    if (previewLoading) {
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}>
          Cargando previsualización...
        </div>
      );
    }

    if (previewError) {
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '20px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px' }}>❌</div>
          <div>{previewError}</div>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#007bff',
              textDecoration: 'none',
              padding: '12px 24px',
              background: '#f0f8ff',
              borderRadius: '6px',
              border: '1px solid #007bff',
              fontWeight: 'bold'
            }}
          >
            📥 Abrir documento
          </a>
        </div>
      );
    }

    const url = previewBlobUrl || fileUrl;

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {canFullscreen && previewBlobUrl && (
          <button
            onClick={toggleFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}
          >
            {isFullscreen ? '📱' : '🔍'}
          </button>
        )}
        
        {isImage && (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5'
          }}>
            <img 
              src={url} 
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
        
        {isPDF && (
          <iframe 
            src={url} 
            title={file.name}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        )}
        
        {!isImage && !isPDF && (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '20px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px' }}>📄</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{file.name}</div>
            <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '300px' }}>
              Previsualización no disponible para este tipo de archivo
            </div>
            {file.openUrl && (
              <a
                href={file.openUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#28a745',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  background: '#f0fff0',
                  borderRadius: '4px',
                  border: '1px solid #28a745'
                }}
              >
                🖥️ Abrir con aplicación local ↗
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={handleClose}>
        <div className={`${styles.modal} ${selectedFile && !isFullscreen ? styles.modalWithViewer : ''}`} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={styles.header}>
            <h3 className={styles.title}>Declaración Responsable Verifactu</h3>
            <button className={styles.closeButton} onClick={handleClose}>
              ✕
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Cargando archivos...</p>
              </div>
            ) : files.length === 0 ? (
              <div className={styles.noFiles}>
                <MyIcons name="fileFilled" width={64} height={64} color="var(--dark-grey)" />
                <p>No hay archivos disponibles</p>
              </div>
            ) : (
              <div className={styles.filesContainer}>
                {!selectedFile ? (
                  // Vista de lista de archivos
                  <div className={styles.filesGrid}>
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className={styles.fileItem}
                        onClick={() => handleFileClick(file)}
                      >
                        <div className={styles.fileIconContainer}>
                          {getFileIcon(file.name)}
                        </div>
                        <div className={styles.fileName}>{file.name}</div>
                        <div className={styles.fileType}>{file.type}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Vista del archivo seleccionado
                  <div className={styles.viewerContainer}>
                    <div className={styles.viewerHeader}>
                      <button className={styles.backButton} onClick={handleCloseViewer}>
                        <MyIcons name="arrowLeft" width={20} height={20} color="var(--black)" />
                        <span>Volver</span>
                      </button>
                      <h4 className={styles.viewerTitle}>{selectedFile.name}</h4>
                    </div>
                    <div className={styles.viewerContent}>
                      <FileViewer file={selectedFile} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!selectedFile && (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={handleClose}>
                Cerrar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen viewer */}
      {selectedFile && isFullscreen && (
        <div className={styles.fullscreenOverlay}>
          <div className={styles.fullscreenContainer}>
            <div className={styles.fullscreenHeader}>
              <h4 className={styles.fullscreenTitle}>{selectedFile.name}</h4>
              <button
                onClick={handleCloseViewer}
                className={styles.fullscreenCloseButton}
              >
                ✕ Cerrar
              </button>
            </div>
            <div className={styles.fullscreenContent}>
              <FileViewer file={selectedFile} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
